import { api, extractListItems, extractPaging, extractResponsePaging, normalizePaging, type ApiResponse } from '../client';

export enum PayoutStatusEnum {
    PENDING = 'Pending',
    COMPLETED = 'Completed',
    FAILED = 'Failed'
}

export interface DonationPayout {
    uuid: string;
    payout_id: string;
    payout_date: string;
    total_amount: string;
    payment_received?: number; // Gross amount received
    l2g_fee_percentage?: number;
    l2g_fee_amount?: number;
    net_payment?: number; // Net payment to charity
    payment_reference?: string; // L2G payment reference
    total_donations: number;
    transaction_date: string;
    transaction_id?: string;
    created_at: string;
    company: string;
    employer?: {
        uuid: string;
        name: string;
    };
    employer_payment_uuid?: string;
    bank_account?: {
        bsb_no?: string | null;
        acc_no?: string | null;
        acc_name?: string | null;
    };
    status: {
        uuid: string;
        name: string;
    };
    processed_by?: {
        uuid: string | null;
        name: string | null;
    };
    payment_method?: string;
    donation_breakdown?: {
        data: Array<{   
            charity: {
                uuid: string;
                name: string;
            };
            donation_request_status: {
                uuid: string;
                name: string;
            };
            employer: {
                uuid: string;
                name: string;
                employer_top_up: number;
            };
            user: {
                uuid: string;
                name: string;
            };
            user_status: {
                uuid: string;
                name: string;
            };
            leave_hours: number;
            leave_value: number;
            rejected_at: string | null;
            rejection_reason: string | null;
            total_donation: number;
            updated_at: string;
        }>;
        paging: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    };
    donation_requests?: Array<{
        uuid: string;
        user?: {
            uuid: string;
            name: string;
        };
        leave_hours: number;
        leave_value: string;
        created_at: string;
        updated_at: string;
    }>;
    amount?: string; // For backward compatibility
    charity?: {
        uuid: string;
        name: string;
    };
    donation_payout_status?: {
        uuid: string;
        name: string;
    };
    /** When the charity marked the payout as received (money in bank). */
    charity_marked_received_at?: string | null;
    /** Convenience: true if charity has marked this payout as received. */
    received?: boolean;
}

export interface PayoutFilters {
    status?: string;
    charity?: string;
    employer?: string;
    /** Filter by charity marked as received: true = received only, false = not received only, undefined = all */
    received?: boolean;
    search?: string;
    page?: number;
    per_page?: number;
    start_date?: string;
    end_date?: string;
}

export interface PaginationInfo {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
}

export interface DonationPayoutsResponse {
    success: boolean;
    data: DonationPayout[];
    paging: PaginationInfo;
    message?: string;
}

export interface ApiSuccessResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export interface DonationPayoutDetail extends DonationPayout {
    donations?: Array<{
        uuid: string;
        amount: string;
        donation_date: string;
        employee?: {
            uuid: string;
            name: string;
            email?: string;
        };
        employer?: {
            uuid: string;
            name: string;
        };
    }>;
}

export interface PayoutDetailResponse {
    success: boolean;
    data: DonationPayoutDetail;
    message?: string;
}

// API base URL
const API_BASE_URL = '/donation-payout';

// Hardcoded status options
const STATUSES = [
    { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e640', name: PayoutStatusEnum.PENDING },
    { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e641', name: PayoutStatusEnum.COMPLETED },
    { uuid: '3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e642', name: PayoutStatusEnum.FAILED }
];

export const PayoutService = {
    /**
     * Get all payouts with filters and pagination
     */
    getPayouts: async (filters: PayoutFilters = {}): Promise<{
        payouts?: DonationPayout[];
        paging?: PaginationInfo;
        success: boolean;
        error?: any;
    }> => {
        try {
            // Build query parameters
            const queryParams = new URLSearchParams();
            
            if (filters.status && filters.status !== 'all') {
                queryParams.append('status', filters.status);
            }

            if (filters.charity && filters.charity !== 'all') {
                queryParams.append('charity', filters.charity);
            }

            if (filters.employer && filters.employer !== 'all') {
                queryParams.append('employer_uuid', filters.employer);
            }

            if (filters.search && filters.search !== 'all') {
                queryParams.append('phrase', filters.search);
            }
            
            if (filters.start_date) {
                queryParams.append('start_date', filters.start_date);
            }
            
            if (filters.end_date) {
                queryParams.append('end_date', filters.end_date);
            }

            if (filters.received !== undefined && filters.received !== null) {
                queryParams.append('received', filters.received ? '1' : '0');
            }
            
            queryParams.append('page', filters.page?.toString() || '1');
            queryParams.append('per_page', filters.per_page?.toString() || '10');
            
            // Make API request
            const url = `${API_BASE_URL}?${queryParams.toString()}`;
            const response = await api.get<DonationPayoutsResponse>(url);
            
            if (response.data && response.data.success) {
                const items = extractListItems<DonationPayout>(response.data.data);
                const paging = normalizePaging(
                    extractResponsePaging(response) ?? extractPaging(response.data.data),
                    items.length
                ) as PaginationInfo | undefined;
                return {
                    payouts: items,
                    paging,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to fetch payouts'
                };
            }
        } catch (error) {
            console.error('Error fetching payouts:', error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get a single payout by UUID
     */
    getPayout: async (uuid: string): Promise<{
        payout?: DonationPayout;
        success: boolean;
        error?: any;
    }> => {
        try {
            const url = `${API_BASE_URL}/${uuid}`;
            const response = await api.get<ApiSuccessResponse<DonationPayout>>(url);
            
            if (response.data && response.data.success) {
                return {
                    payout: response.data.data,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Payout not found'
                };
            }
        } catch (error) {
            console.error(`Error fetching payout ${uuid}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get all available statuses for filtering
     */
    getStatuses: async (): Promise<{
        statuses: Array<{ uuid: string; name: string }>;
        success: boolean;
    }> => {
        // Return hardcoded statuses
        return {
            statuses: STATUSES,
            success: true
        };
    },

    /**
     * Get all available charities for filtering
     */
    getCharities: async (): Promise<{
        charities?: Array<{ uuid: string; name: string }>;
        success: boolean;
        error?: any;
    }> => {
        try {
            const url = '/api/v1/charity';
            const response = await api.get<ApiSuccessResponse<Array<{ uuid: string; name: string }>>>(url);
            
            if (response.data && response.data.success) {
                return {
                    charities: response.data.data,
                    success: true
                };
            } else {
                return {
                    charities: [],
                    success: false,
                    error: response.data?.message || 'Failed to fetch charities'
                };
            }
        } catch (error) {
            console.error('Error fetching charities:', error);
            return {
                charities: [],
                success: false,
                error
            };
        }
    },

    /**
     * Process a payout
     */
    processPayout: async (payoutId: string): Promise<{
        success: boolean;
        error?: any;
    }> => {
        try {
            const url = `${API_BASE_URL}/${payoutId}/process`;
            const response = await api.post<ApiSuccessResponse<any>>(url);
            
            return {
                success: response.data.success,
                error: response.data.success ? undefined : response.data.message
            };
        } catch (error) {
            console.error(`Error processing payout ${payoutId}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get detailed information about a payout including all associated donations
     */
    getPayoutDetail: async (uuid: string, page: number = 1, perPage: number = 10): Promise<{
        payoutDetail?: DonationPayout;
        success: boolean;
        error?: any;
    }> => {
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('page', page.toString());
            queryParams.append('per_page', perPage.toString());
            
            const url = `${API_BASE_URL}/${uuid}?${queryParams.toString()}`;
            const response = await api.get<ApiSuccessResponse<DonationPayout>>(url);
            
            if (response.data && response.data.success) {
                return {
                    payoutDetail: response.data.data,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Payout details not found'
                };
            }
        } catch (error) {
            console.error(`Error fetching payout details for ${uuid}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Mark a payout as received by the charity (money in bank).
     * Only valid for completed payouts.
     */
    markAsReceived: async (uuid: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const url = `${API_BASE_URL}/${uuid}/mark-received`;
            const response = await api.post<ApiSuccessResponse<DonationPayout>>(url);
            if (response.data?.success) {
                return { success: true };
            }
            return {
                success: false,
                error: (response.data as { message?: string })?.message || 'Failed to mark as received'
            };
        } catch (err: unknown) {
            const message = err && typeof err === 'object' && 'response' in err
                ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
                : null;
            return {
                success: false,
                error: message || 'Failed to mark as received'
            };
        }
    }
};
